import { Prompts } from '@ant-design/x'
import { theme, Typography } from 'antd'
import { Sparkles } from 'lucide-react'
import { motion } from 'motion/react'
import type { Transition } from 'motion/react'
import { forwardRef, useMemo } from 'react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import type { OrganizationRole } from 'src/api/main'
import { IconWithBackground } from 'src/components'
import {
  ChatComposer,
  useStyles,
  type ChatComposerExtras,
  type ChatComposerHandle
} from 'src/components/chat'

import { getChatSuggestions, SUGGESTION_ICON_BOX } from './suggestions'

const { Text, Title } = Typography

const ENTRANCE_STAGGER = 0.06
const BRAND_ICON_GLYPH = 26

type Props = {
  greeting: string
  firstName: string
  role: OrganizationRole
  input: string
  onChange: (value: string) => void
  onSend: () => void
  onStop: () => void
  streaming: boolean
  disabled?: boolean
  /** The agent's own face. Omitted, the brand plate stands in for it. */
  avatar?: ReactNode
  onPickSuggestion: (prompt: string) => void
  composerExtras?: ChatComposerExtras
}

export const ChatWelcome = forwardRef<ChatComposerHandle, Props>(
  function ChatWelcome(
    {
      greeting,
      firstName,
      role,
      input,
      onChange,
      onSend,
      onStop,
      streaming,
      disabled,
      avatar,
      onPickSuggestion,
      composerExtras
    },
    ref
  ) {
    const { styles } = useStyles()
    const { t } = useTranslation()
    const { token } = theme.useToken()

    const suggestionItems = useMemo(
      () =>
        getChatSuggestions(role).map(({ localeKey, icon }) => ({
          key: localeKey,
          icon: (
            <IconWithBackground
              color={token.colorPrimary}
              size={SUGGESTION_ICON_BOX}
            >
              {icon}
            </IconWithBackground>
          ),
          label: t(`workspaceHome.suggestions.${localeKey}`)
        })),
      [role, t, token.colorPrimary]
    )

    const fadeUp = (delay: number) => ({
      initial: { opacity: 0, y: 12 },
      animate: { opacity: 1, y: 0 },
      transition: {
        duration: parseFloat(token.motionDurationMid),
        ease: 'easeOut',
        delay
      } satisfies Transition
    })

    return (
      <div className={styles.welcomeStack}>
        <motion.span
          className={avatar ? undefined : styles.welcomeAvatar}
          aria-hidden="true"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{
            duration: parseFloat(token.motionDurationMid),
            ease: 'easeOut'
          }}
        >
          {avatar ?? <Sparkles size={BRAND_ICON_GLYPH} strokeWidth={1.5} />}
        </motion.span>

        <div className={styles.welcomeCopy}>
          <motion.div {...fadeUp(ENTRANCE_STAGGER)}>
            <Text type="secondary" className={styles.welcomeGreeting}>
              {greeting}
              {firstName ? `, ${firstName}` : ''}
            </Text>
          </motion.div>
          <motion.div {...fadeUp(ENTRANCE_STAGGER * 2)}>
            <Title level={2} className={styles.welcomeTitle}>
              {t('workspaceHome.welcome.title')}
            </Title>
          </motion.div>
          <motion.div {...fadeUp(ENTRANCE_STAGGER * 3)}>
            <Text type="secondary" className={styles.welcomeSubtitle}>
              {t('workspaceHome.welcome.subtitle')}
            </Text>
          </motion.div>
        </div>

        <motion.div
          className={styles.welcomeComposerWrap}
          {...fadeUp(ENTRANCE_STAGGER * 4)}
        >
          <ChatComposer
            ref={ref}
            className={styles.welcomeComposer}
            value={input}
            onChange={onChange}
            onSend={onSend}
            onStop={onStop}
            streaming={streaming}
            disabled={disabled}
            {...composerExtras}
          />
        </motion.div>

        <Prompts
          wrap
          fadeInLeft
          items={suggestionItems}
          className={styles.suggestionsPrompts}
          classNames={{
            list: styles.suggestionsList,
            item: styles.suggestionItem
          }}
          onItemClick={(info) =>
            onPickSuggestion(t(`workspaceHome.suggestions.${info.data.key}`))
          }
        />
      </div>
    )
  }
)
